/**
 * Brand Kits API
 * API functions for managing brand kit data within account groups
 */

import { skedApi } from "../../../api/_skedApi";
import { GeneratedSkedApiClient } from "../../../api/generated-client";
import type { paths } from "../../../api/generated/sked-api-openapi-types";
import { ALLOWED_DOCUMENT_TYPES } from "../schemas";
import {
  AccountGroupWithBrandKit,
  UpdateBrandDescriptionPayload,
  UpdateBrandAssetsPayload,
  UpdateCustomInstructionsPayload,
  CreateToneProfilePayload,
  UpdateToneProfilePayload,
  ToneOfVoiceProfile,
  BrandKit,
  CustomInstructions,
} from "../types";

const BASE_URL = "/account-groups";

type GetAccountGroupResponse =
  paths["/account-groups/{groupId}"]["get"]["responses"][200]["content"]["application/json"];
type UpdateBrandDescriptionOperation =
  paths["/account-groups/{groupId}/brand-kit/description"]["patch"];
type UpdateBrandDescriptionBody = NonNullable<
  UpdateBrandDescriptionOperation["requestBody"]
>["content"]["application/json"];
type UpdateBrandDescriptionResponse =
  UpdateBrandDescriptionOperation["responses"][200]["content"]["application/json"];
type UpdateCustomInstructionsOperation =
  paths["/account-groups/{groupId}/brand-kit/custom-instructions"]["patch"];
type UpdateCustomInstructionsBody = NonNullable<
  UpdateCustomInstructionsOperation["requestBody"]
>["content"]["application/json"];
type UpdateCustomInstructionsResponse =
  UpdateCustomInstructionsOperation["responses"][200]["content"]["application/json"];

function getGeneratedSkedApiClient() {
  return new GeneratedSkedApiClient<paths>();
}

export interface GenerateCaptionSuggestionsPayload {
  instruction?: string;
  caption?: string;
  platforms: string[];
  labelIds?: string[];
  toneOfVoiceProfileId?: string;
  includeEmoji?: boolean;
  mediaDescriptions?: string[];
  suggestionCount?: number;
}

export interface CaptionSuggestion {
  caption: string;
  warnings: string[];
  appliedConstraints: {
    maxLength: number | null;
    platforms: string[];
    includeEmoji: boolean;
    toneOfVoiceApplied: boolean;
    labelCount: number;
    mediaDescriptionCount: number;
  };
}

/**
 * Helper to build FormData for tone profile API calls
 */
function buildToneProfileFormData(
  payload: CreateToneProfilePayload | UpdateToneProfilePayload
): FormData {
  const formData = new FormData();

  if (payload.name !== undefined) {
    formData.append("name", payload.name);
  }

  if (payload.description !== undefined) {
    formData.append("description", payload.description);
  }

  if (payload.referenceUrls && payload.referenceUrls.length > 0) {
    formData.append("referenceUrls", JSON.stringify(payload.referenceUrls));
  }

  if (payload.additionalText) {
    formData.append("additionalText", payload.additionalText);
  }

  if (payload.documents) {
    for (const file of payload.documents) {
      if (!ALLOWED_DOCUMENT_TYPES.includes(file.type)) {
        throw new Error(`Invalid file type: ${file.type}`);
      }
      formData.append("documents", file);
    }
  }

  if ("regenerate" in payload && payload.regenerate) {
    formData.append("regenerate", "true");
  }

  return formData;
}

/**
 * Get account group with brand kit data
 */
export async function getAccountGroupWithBrandKit(
  groupId: string,
  signal?: AbortSignal
): Promise<AccountGroupWithBrandKit> {
  const generatedClient = getGeneratedSkedApiClient();
  const result = await generatedClient.client.GET("/account-groups/{groupId}", {
    params: {
      path: { groupId },
    },
    signal,
  });

  return (await generatedClient.unwrap(
    result
  )) as GetAccountGroupResponse as unknown as AccountGroupWithBrandKit;
}

/**
 * Update brand description
 * Calls Relevance AI synchronously and returns the updated brand kit
 */
export async function updateBrandDescription(
  groupId: string,
  payload: UpdateBrandDescriptionPayload
): Promise<BrandKit> {
  const generatedClient = getGeneratedSkedApiClient();
  const result = await generatedClient.client.PATCH(
    "/account-groups/{groupId}/brand-kit/description",
    {
      params: {
        path: { groupId },
      },
      body: payload satisfies UpdateBrandDescriptionBody,
    }
  );
  const data = (await generatedClient.unwrap(
    result
  )) as UpdateBrandDescriptionResponse;

  return data.brandKit as unknown as BrandKit;
}

export async function generateCaptionSuggestions(
  groupId: string,
  payload: GenerateCaptionSuggestionsPayload
): Promise<CaptionSuggestion[]> {
  const response = await skedApi.post<{ suggestions: CaptionSuggestion[] }>(
    `${BASE_URL}/${groupId}/brand-kit/caption-suggestions`,
    payload
  );

  return response.data.suggestions || [];
}

/**
 * Update brand assets (logo, colors, fonts)
 */
export async function updateBrandAssets(
  groupId: string,
  payload: UpdateBrandAssetsPayload
): Promise<BrandKit> {
  const response = await skedApi.patch<{ brandKit: BrandKit }>(
    `${BASE_URL}/${groupId}/brand-kit/assets`,
    payload
  );
  return response.data.brandKit;
}

/**
 * Create a new tone of voice profile
 * Uses multipart/form-data with direct file uploads
 */
export async function createToneProfile(
  groupId: string,
  payload: CreateToneProfilePayload
): Promise<ToneOfVoiceProfile> {
  const formData = buildToneProfileFormData(payload);
  const response = await skedApi.post<{
    toneOfVoiceProfile: ToneOfVoiceProfile;
  }>(`${BASE_URL}/${groupId}/brand-kit/tone-profiles`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data.toneOfVoiceProfile;
}

/**
 * Update an existing tone of voice profile
 * Uses multipart/form-data with direct file uploads
 */
export async function updateToneProfile(
  groupId: string,
  profileId: string,
  payload: UpdateToneProfilePayload
): Promise<ToneOfVoiceProfile> {
  const formData = buildToneProfileFormData(payload);
  const response = await skedApi.patch<{
    toneOfVoiceProfile: ToneOfVoiceProfile;
  }>(`${BASE_URL}/${groupId}/brand-kit/tone-profiles/${profileId}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data.toneOfVoiceProfile;
}

/**
 * Delete a tone of voice profile
 */
export async function deleteToneProfile(
  groupId: string,
  profileId: string
): Promise<void> {
  await skedApi.delete(
    `${BASE_URL}/${groupId}/brand-kit/tone-profiles/${profileId}`
  );
}

/**
 * Update custom instructions
 * Supports partial updates - only provided fields are updated
 */
export async function updateCustomInstructions(
  groupId: string,
  payload: UpdateCustomInstructionsPayload
): Promise<CustomInstructions> {
  const generatedClient = getGeneratedSkedApiClient();
  const result = await generatedClient.client.PATCH(
    "/account-groups/{groupId}/brand-kit/custom-instructions",
    {
      params: {
        path: { groupId },
      },
      body: payload satisfies UpdateCustomInstructionsBody,
    }
  );
  const data = (await generatedClient.unwrap(
    result
  )) as UpdateCustomInstructionsResponse;

  return data.customInstructions as unknown as CustomInstructions;
}
